import { FC, ReactElement, cloneElement, memo, useCallback, useEffect, useState } from 'react';

import { AttachmentResponse, PostgresFilePointer } from '@/api/data-contracts';
import FileDialog from '@/features/media/ui/FileManager/FileDialog';
import FileManagerButton from '@/features/media/ui/FileManager/FileManagerButton';
import useVersions from '@/features/media/ui/FileManager/useVersions';
import useFiles from '@/shared/api/files.service';

interface Props {
  attachments: AttachmentResponse[];
  mediaPlanId?: string;
  orderId?: string;
  advertisingSpaceId?: string;
  bookingId?: string;
  taskId?: string;
  children?: ReactElement;
  open?: boolean;
  onClose?: () => void;
  disableVersionUpload?: boolean;
  category?: 'keyVisual' | 'artWork';
}

const FileManager: FC<Props> = ({
  attachments,
  mediaPlanId,
  orderId,
  advertisingSpaceId,
  bookingId,
  taskId,
  children,
  open,
  onClose,
  disableVersionUpload,
  category,
}) => {
  const { downloadAttachments } = useVersions();
  const { downloadThumbnail } = useFiles();

  const [isDialogOpen, setDialogOpen] = useState<boolean>(open ?? false);
  const [files, setFiles] = useState<File[]>([]);
  const [thumbnails, setThumbnails] = useState<File[]>([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState<number>(0);

  useEffect(() => {
    if (open) {
      setDialogOpen(open);
    }
  }, [open]);

  const downloadThumbnails = useCallback(async () => {
    const thumbs: File[] = [];
    await Promise.all(
      attachments
        .map((a) => a.filePointer)
        .map(async (item: PostgresFilePointer, index: number) => {
          const blob = await downloadThumbnail(item.objectId!);
          thumbs[index] = new File([blob], item.name as string);

          return Promise.resolve();
        }),
    );

    setThumbnails(thumbs);
  }, [attachments, downloadThumbnail]);

  useEffect(() => {
    if (isDialogOpen && attachments.length) {
      downloadAttachments(
        attachments.map((a) => a.filePointer),
        {
          orderId,
          mediaPlanId,
          bookingId,
          advertisingSpaceId,
        },
      ).then((response) => {
        setFiles(response);
        if (response?.length) {
          downloadThumbnails();
        }
      });
    }
  }, [
    advertisingSpaceId,
    attachments,
    attachments.length,
    bookingId,
    downloadAttachments,
    downloadThumbnails,
    isDialogOpen,
    mediaPlanId,
    orderId,
  ]);

  const handleDialogOpen = useCallback((index?: number) => {
    setDefaultImageIndex(index || 0);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    onClose?.();
  }, [onClose]);

  return (
    <>
      {children ? (
        // @ts-ignore
        cloneElement(children as ReactElement, { onImageClick: handleDialogOpen })
      ) : (
        <FileManagerButton filesCount={attachments?.length ?? 0} handleOpen={handleDialogOpen} />
      )}

      {!!files?.length && isDialogOpen && (
        <FileDialog
          attachments={attachments}
          files={files}
          setFiles={setFiles}
          mediaPlanId={mediaPlanId}
          orderId={orderId}
          taskId={taskId}
          bookingId={bookingId}
          advertisingSpaceId={advertisingSpaceId}
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          defaultImageIndex={defaultImageIndex}
          disableVersionUpload={disableVersionUpload}
          category={category}
          thumbnails={thumbnails}
        />
      )}
    </>
  );
};

export default memo(FileManager, (prevProps, nextProps) => {
  const attachmentsEqual =
    prevProps.attachments === nextProps.attachments ||
    (prevProps.attachments?.length === nextProps.attachments?.length &&
      prevProps.attachments?.every(
        (attachment, index) =>
          attachment.id === nextProps.attachments?.[index]?.id &&
          attachment.activeVersion === nextProps.attachments?.[index]?.activeVersion,
      )) ||
    false;

  return (
    attachmentsEqual &&
    prevProps.mediaPlanId === nextProps.mediaPlanId &&
    prevProps.orderId === nextProps.orderId &&
    prevProps.advertisingSpaceId === nextProps.advertisingSpaceId &&
    prevProps.bookingId === nextProps.bookingId &&
    prevProps.taskId === nextProps.taskId &&
    prevProps.open === nextProps.open &&
    prevProps.disableVersionUpload === nextProps.disableVersionUpload &&
    prevProps.category === nextProps.category &&
    prevProps.children === nextProps.children
  );
});
